/**
 * SSE Client for POST requests using fetch + ReadableStream
 *
 * Handles Server-Sent Events (SSE) for streaming AI responses.
 * SSE format: event: <type>\ndata: <json>\n\n
 */

import { config } from "@/config";
import { metricsRequestHeaders } from "../lib/metrics/requestHeaders";

const apiURL = config.apiUrl;

const messageOf = (value: unknown): string | null => {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "object" && value !== null && "msg" in value) {
    return messageOf(value.msg);
  }
  if (typeof value === "object" && value !== null && "message" in value) {
    return messageOf(value.message);
  }
  return null;
};

/**
 * Turns an error field into display text, or null. In production a FastAPI 422
 * sends `detail` as a list of `{type, loc, msg, ...}`; that becomes its distinct
 * `msg` values joined by "; ". An object with a `message` (the `{code, message}`
 * refusal shape REST routes use) yields that message. Only a string may reach a
 * toast: an object is not a valid React child and unmounts the app.
 */
const errorDetailToMessage = (detail: unknown): string | null => {
  if (!Array.isArray(detail)) return messageOf(detail);
  const messages = [
    ...new Set(detail.map(messageOf).filter((m): m is string => m !== null)),
  ];
  return messages.length ? messages.join("; ") : null;
};

export interface SSEChunkData {
  content: string;
  is_final: boolean;
}

export interface SSEResultData {
  plan?: unknown;
  control_id?: string;
  [key: string]: unknown;
}

export interface ToolEvent {
  toolCallId: string | null;
  toolName: string;
  displayName: string;
  result: string | null;
  documentName: string | null;
}

export interface AgentCall {
  agentName: string;
}

/**
 * Midship drafting the tests of a turn's analytics columns, before its result.
 * Sent as drafting starts and again when it ends, only when a model is asked;
 * if drafting itself errors, the stream ends with an error instead. Follows the
 * agent's turn when there is one.
 */
export interface DraftingProgress {
  /**
   * The start lists every column being drafted. Done lists only the columns
   * that received a drafted test; it may be empty.
   */
  columnNames: string[];
  done: boolean;
}

/**
 * Ordered timeline of agent events emitted during a single stream.
 * Appended in arrival order so the full sequence — tool invocations, their
 * results, and streamed text — can be rendered chronologically.
 *
 * Lives here rather than in a collab hook because it is only a union over the
 * event shapes above, and every collab flow renders the same timeline.
 */
export type AgentEvent =
  | { type: "tool_call"; toolEvent: ToolEvent }
  | { type: "tool_result"; toolEvent: ToolEvent }
  | { type: "agent_call"; agentCall: AgentCall }
  | { type: "drafting_progress"; drafting: DraftingProgress };

export interface SSECallbacks {
  onChunk: (content: string, isFinal: boolean) => void;
  onToolCall: (toolEvent: ToolEvent) => void;
  onToolResult: (toolEvent: ToolEvent) => void;
  onAgentCall: (agentCall: AgentCall) => void;
  onHeartbeat: (count: number) => void;
  onDraftingProgress: (drafting: DraftingProgress) => void;
  onResult: (result: SSEResultData) => void;
  /** `status` is set when the request was answered with an HTTP error before any stream began. */
  onError: (error: string, status?: number) => void;
  onComplete?: () => void;
  /** Names the run behind this stream, so it can be resumed if the stream dies. */
  onRun?: (runId: string) => void;
  /** The relay ended while its run is still working — re-attach rather than fail. */
  onReconnect?: (runId: string) => void;
  /**
   * The run is over and reported to whoever was listening at the time.
   *
   * A subscription replays nothing, so re-attaching after a run ends yields an
   * empty stream. The work succeeded and the server holds the result; there is
   * nothing to retry and nothing to report as broken.
   */
  onFinished?: (runId: string) => void;
  /**
   * The connection itself failed, as opposed to the server reporting a failure.
   *
   * Worth separating because only one of them says anything about the work: a
   * server `error` is the run's own verdict and is final, while a dead socket
   * says nothing at all — the run it was carrying usually outlives it. Falls
   * back to `onError` when not given.
   */
  onTransportError?: (error: string) => void;
}

export interface SSEOptions {
  method?: "POST" | "GET";
  body?: Record<string, unknown>;
}

interface SSEEvent {
  event: string;
  data: string;
}

function parseSSELine(line: string): { key: string; value: string } | null {
  const colonIndex = line.indexOf(":");
  if (colonIndex === -1) return null;

  const key = line.slice(0, colonIndex).trim();
  const value = line.slice(colonIndex + 1).trim();
  return { key, value };
}

function parseSSEBuffer(buffer: string): {
  events: SSEEvent[];
  remaining: string;
} {
  const events: SSEEvent[] = [];
  const parts = buffer.split(/\r?\n\r?\n/); // sse starlette standard for event delimiters

  // Last part may be incomplete
  const remaining = parts.pop() || "";

  for (const part of parts) {
    if (!part.trim()) continue;

    const lines = part.split("\n");
    let event = "";
    let data = "";

    for (const line of lines) {
      const parsed = parseSSELine(line);
      if (!parsed) continue;

      if (parsed.key === "event") {
        event = parsed.value;
      } else if (parsed.key === "data") {
        data = parsed.value;
      }
    }

    if (event && data) {
      events.push({ event, data });
    }
  }

  return { events, remaining };
}

export interface RawStreamOptions {
  method?: "GET" | "POST";
  body?: Record<string, unknown>;
}

export interface RawStreamHandlers {
  onEvent: (event: string, data: string) => void;
  /** `status` is set when the failure was an HTTP error response (vs network/parse). */
  onError?: (error: string, status?: number) => void;
  onComplete?: () => void;
}

/**
 * Low-level SSE reader that forwards raw (event, data) pairs to a handler.
 *
 * Unlike `fetchSSE` (which is tailored to the collab generation event shapes),
 * this leaves event interpretation to the caller, so it suits arbitrary SSE
 * endpoints such as the document-processing pipeline stream. Supports GET and
 * POST and honours an AbortSignal for teardown.
 */
export async function openEventStream(
  path: string,
  options: RawStreamOptions,
  handlers: RawStreamHandlers,
  abortSignal?: AbortSignal,
): Promise<void> {
  const token = localStorage.getItem("access_token");
  const workspaceId = localStorage.getItem("workspace_id");

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    Authorization: `Bearer ${token}`,
    "X-Workspace-Id": workspaceId || "",
    ...metricsRequestHeaders(),
  };

  try {
    const response = await fetch(`${apiURL}${path}`, {
      method: options.method || "GET",
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: abortSignal,
    });

    if (!response.ok) {
      handlers.onError?.(
        `Request failed with status ${response.status}`,
        response.status,
      );
      return;
    }
    if (!response.body) {
      handlers.onError?.("No response body");
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let done = false;

    while (!done) {
      const result = await reader.read();
      done = result.done;
      buffer += done ? "\n\n" : decoder.decode(result.value, { stream: true });

      const { events, remaining } = parseSSEBuffer(buffer);
      buffer = remaining;

      for (const event of events) {
        handlers.onEvent(event.event, event.data);
      }

      if (done) {
        handlers.onComplete?.();
      }
    }
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      handlers.onComplete?.();
      return;
    }
    handlers.onError?.(
      error instanceof Error ? error.message : "An unexpected error occurred",
    );
  }
}

/** Whether an `onError` status is a 400: the server refused the request, so no work started. */
export function isRefusedRequest(status?: number): boolean {
  return status === 400;
}

export async function fetchSSE(
  path: string,
  options: SSEOptions,
  callbacks: SSECallbacks,
  abortSignal?: AbortSignal,
): Promise<void> {
  const token = localStorage.getItem("access_token");
  const workspaceId = localStorage.getItem("workspace_id");

  const url = `${apiURL}${path}`;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    Authorization: `Bearer ${token}`,
    "X-Workspace-Id": workspaceId || "",
    ...metricsRequestHeaders(),
  };

  try {
    const response = await fetch(url, {
      method: options.method || "POST",
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: abortSignal,
    });

    if (!response.ok) {
      let errorMessage = `Request failed with status ${response.status}`;
      try {
        const errorBody = await response.json();
        errorMessage =
          errorDetailToMessage(errorBody?.detail) ??
          errorDetailToMessage(errorBody?.error_message) ??
          errorMessage;
      } catch {
        // Ignore JSON parse error
      }
      callbacks.onError(errorMessage, response.status);
      return;
    }

    if (!response.body) {
      callbacks.onError("No response body");
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let done = false;

    while (!done) {
      const result = await reader.read();
      done = result.done;

      // Append decoded chunk, or a terminating delimiter on stream end so the
      // final (possibly unterminated) event is flushed by parseSSEBuffer.
      buffer += done ? "\n\n" : decoder.decode(result.value, { stream: true });

      const { events, remaining } = parseSSEBuffer(buffer);
      buffer = remaining;

      for (const event of events) {
        processSSEEvent(event, callbacks);
      }

      if (done) {
        callbacks.onComplete?.();
      }
    }
  } catch (error) {
    const report = callbacks.onTransportError ?? callbacks.onError;
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        callbacks.onComplete?.();
        return;
      }
      report(error.message);
    } else {
      report("An unexpected error occurred");
    }
  }
}

/**
 * Backoff before each re-attach, indexed by attempt.
 *
 * Spans about ninety seconds in total, which is the point: the connection is
 * most often lost because the API went away, and it has to still be gone when
 * the last attempt lands for the reconnect to be worth having. A local restart
 * takes ~18s and a rolling deploy longer, so a window measured in seconds gives
 * up while the thing it is waiting for is still happening — and reports a
 * failure over a run that is alive and about to finish.
 *
 * The tail is generous because attempts against a closed port cost nothing, and
 * a plan takes one to three minutes: a client that comes back late still finds
 * the run, or is told it already finished.
 */
const RESUME_BACKOFF_MS = [500, 2000, 5000, 10000, 15000, 20000, 30000];

/** How many times a lost stream is re-attached before the failure is reported. */
const MAX_RESUME_ATTEMPTS = RESUME_BACKOFF_MS.length;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** A request to re-attach with, or null when this interaction cannot resume. */
export type ResumeRequest = { path: string; options: SSEOptions } | null;

/**
 * Stream an interaction, re-attaching to its run if the connection dies.
 *
 * The API deliberately lets a run outlive the connection that started it, so a
 * broken stream is a connection problem, not a work problem: the run goes on
 * and finishes. Every stream opens by naming its run, and that id is the way
 * back to it.
 *
 * The connection can also die before that name arrives — the window between
 * sending the request and the first frame — which is why `resume` is asked even
 * when there is no run id. What to do then is the caller's to decide, because
 * it depends on the interaction: planning a session is one idempotent operation
 * and can simply be asked for again, which the API answers by attaching to the
 * run already doing it. Sending a message is not, and gives up instead rather
 * than risk saying the same thing twice.
 *
 * Only the connection is retried. A server `error` is the run's own verdict and
 * ends things, as does a non-200 — those describe the work, and repeating them
 * would just fail the same way more slowly.
 *
 * @param path Path to start the interaction on.
 * @param options Method and body for that first request.
 * @param resume Builds the request that re-attaches, given the run id if one
 *   arrived. Returning null gives up.
 * @param callbacks Stream callbacks. `onReconnecting` fires before each retry.
 * @param abortSignal Cancels the stream and any pending retry.
 */
export async function fetchSSEWithResume(
  path: string,
  options: SSEOptions,
  resume: (runId: string | null) => ResumeRequest,
  callbacks: SSECallbacks & { onReconnecting?: (attempt: number) => void },
  abortSignal?: AbortSignal,
): Promise<void> {
  let runId: string | null = null;
  let request = { path, options };
  let attempt = 0;

  for (;;) {
    // Set by any frame that settles the interaction: the run's result, its
    // error, or word that it already finished. Anything else means the stream
    // ended with the run unaccounted for.
    let reported = false;

    await fetchSSE(
      request.path,
      request.options,
      {
        ...callbacks,
        onRun: (id) => {
          runId = id;
          callbacks.onRun?.(id);
        },
        onResult: (result) => {
          reported = true;
          callbacks.onResult(result);
        },
        onError: (error, status) => {
          reported = true;
          callbacks.onError(error, status);
        },
        // Both say the run outlived its connection. Neither settles anything,
        // so neither sets `reported` — the retry below is driven by that.
        onReconnect: () => {},
        onTransportError: () => {},
        // Re-attached after the run ended. Nothing to retry and nothing to
        // report — completing lets the caller's refetch pick up the result.
        onFinished: (id) => {
          reported = true;
          callbacks.onFinished?.(id);
        },
        // Completion is decided out here — a body ending mid-run is the very
        // thing being recovered from, not the end of the interaction.
        onComplete: undefined,
      },
      abortSignal,
    );

    // A stream that ends without settling anything is a lost connection,
    // however it ended. The socket may throw, the server may say the run is
    // still working — or the body may simply stop, which is what a graceful
    // API shutdown looks like from here and is the case this exists for.
    // Reading only the noisy endings would miss the quietest and commonest one.
    const next =
      !reported && !abortSignal?.aborted && attempt < MAX_RESUME_ATTEMPTS
        ? resume(runId)
        : null;

    if (!next) {
      if (!reported && !abortSignal?.aborted) {
        callbacks.onError(
          "Lost connection. Please refresh to pick up where this left off.",
        );
      }
      callbacks.onComplete?.();
      return;
    }

    callbacks.onReconnecting?.(attempt + 1);
    await sleep(RESUME_BACKOFF_MS[attempt]);
    attempt += 1;
    request = next;
  }
}

function processSSEEvent(event: SSEEvent, callbacks: SSECallbacks): void {
  try {
    const data = JSON.parse(event.data);

    switch (event.event) {
      case "chunk":
        callbacks.onChunk(data.content || "", data.is_final || false);
        break;
      case "result":
        callbacks.onResult(data);
        break;
      case "error":
        callbacks.onError(
          errorDetailToMessage(data.message) ??
            errorDetailToMessage(data.error) ??
            "Unknown error",
        );
        break;
      case "complete":
        callbacks.onComplete?.();
        break;

      //agent activity related events
      case "tool_call":
      case "tool_result": {
        const toolEvent: ToolEvent = {
          toolCallId: data.tool_call_id ?? null,
          toolName: data.tool_name,
          displayName: data.display_name,
          result: data.result,
          documentName: data.document_name ?? null,
        };
        if (event.event === "tool_call") {
          callbacks.onToolCall(toolEvent);
        } else {
          callbacks.onToolResult(toolEvent);
        }
        break;
      }
      case "agent_call":
        callbacks.onAgentCall({
          agentName: data.agent_name,
        });
        break;
      case "heartbeat":
        callbacks.onHeartbeat(data.count);
        break;
      case "drafting_progress":
        callbacks.onDraftingProgress({
          columnNames: Array.isArray(data.column_names)
            ? data.column_names
            : [],
          done: Boolean(data.done),
        });
        break;
      case "run":
        callbacks.onRun?.(data.run_id);
        break;
      case "reconnect":
        callbacks.onReconnect?.(data.run_id);
        break;
      case "finished":
        callbacks.onFinished?.(data.run_id);
        break;
    }
  } catch {
    console.error("Failed to parse SSE data:", event.data);
  }
}
